import { Switch } from '@headlessui/react';
import { pdf } from '@react-pdf/renderer';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import useDeepCompareEffect from 'use-deep-compare-effect';
import ProductPDFDocument from '../components/ProductPdf';
import { categories } from '../data/categories';
import ContentSummary from './Content/ContentSummary';

import { SearchOutlined } from '@ant-design/icons';
import {
  CalendarDaysIcon,
  DocumentChartBarIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Collapse,
  ConfigProvider,
  DatePicker,
  Divider,
  Input,
  Modal,
  Select,
  Table,
  Tooltip,
  Tour,
  Tree,
  TreeSelect,
} from 'antd';
import dayjs from 'dayjs';
import { aggregators } from '../data/aggregators';
import { contentTypesCondensed } from '../data/contentTypes';
import { redemptionTypes } from '../data/redemptionTypes';
import { sports } from '../data/sports';
import { subcategories } from '../data/subcategories';
import { supabase } from '../supabaseClient';
import {
  hasExportPrivileges,
  isAdmin,
  regionMapping,
  useUser,
} from '../UserProvider';
import {
  classNames,
  cleanString,
  convertToCSV,
  formatDateMedium,
  localeDateFormat,
} from '../utils';
import ProductInfoView from './Content/ProductInfoView';

import { debounce } from 'lodash';
import { specialContentTypeStrings } from './Content/SubmitContentPage';
import { PaginatedItems } from './PaginatedItems';

const { SHOW_PARENT } = TreeSelect;

// Count pill shown on a collapsed filter section's header so active selections
// stay visible even when the section is closed.
const PanelBadge = ({ count }) =>
  count > 0 ? (
    <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-orange-400 px-1.5 py-0.5 text-sm font-semibold leading-none text-white">
      {count}
    </span>
  ) : null;

const SearchResults = () => {
  const location = useLocation();

  const {
    user,
    languages,
    countriesByRegion,
    sponsorships,
    programs,
    cardTypes,
  } = useUser();

  const sponsorshipsWithSelectAll = [
    { label: 'Select All', value: 'all' },
    ...sponsorships,
  ];

  // Optionally provided on navigation via location.state. May be null when the
  // page is opened directly (e.g. "Explore Content Catalog").
  const specialProductTypes = Array.isArray(location.state?.specialProductTypes)
    ? location.state.specialProductTypes
    : [];

  const emptyState = {
    contentStatus: [],
    availabilityRange: null,
    activationMonth: null,
    category: [],
    contentType: [],
    countries: [],
    globalAvailability: [],
    calculatedImplementationTypes: [],
    ptr: false,
    ptr2: [],
    redemptionType: [],
    subcategories: [],
    priceGroup: false,
    ecoFriendly: false,
    isIconic: false,
    epic: false,
    privilegedAccess: false,
    specialTreat: false,
    languages: [],
    linkedSponsorships: [],
    programNames: [],
    excludeProgramNames: [],
    cardTypeNames: [],
    aggregators: [],
    mastercardCollection: false,
    mastercardCollectionTypes: [],
    exclude_mastercard_collection: false,
    exclude_content_aggregators: 'EXCLUDE_CONTENT_AGGREGATORS',
    exclude_benefits: 'EXCLUDE_BENEFITS',
    exclude_travel_benefits: 'EXCLUDE_TRAVEL_BENEFITS',
  };

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    unregister,
    formState: { isDirty, errors },
  } = useForm({
    mode: 'onChange', // or you can use "onBlur"
    defaultValues: location.state
      ? {
          contentStatus: location.state.contentStatus,
          countries: location.state.countries,
          globalAvailability: location.state.globalAvailability,
          ix: location.state.ix,
          ptr: location.state.ptr === 'PTR_ALLOWED',
          ptr2: location.state.ptr2,
          availabilityRange: null,
          activationMonth: location.state.activationMonth
            ? dayjs(location.state.activationMonth)
            : null,
          category: location.state.category,
          sports: location.state.sports ?? [],
          contentType: location.state.contentType,
          languages: location.state.languages,

          ecoFriendly: specialProductTypes.includes(
            specialContentTypeStrings.ecoFriendly,
          ),
          isIconic: specialProductTypes.includes(
            specialContentTypeStrings.iconic,
          ),
          epic: specialProductTypes.includes(specialContentTypeStrings.epic),
          privilegedAccess: specialProductTypes.includes(
            specialContentTypeStrings.privilegedAccess,
          ),
          specialTreat: specialProductTypes.includes(
            specialContentTypeStrings.specialTreat,
          ),
        }
      : {},
  });

  const [loading, setLoading] = useState(true);
  const [isProductInfoVisible, setProductInfoVisible] = useState(false);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [activeFilterPanels, setActiveFilterPanels] = useState([]);
  const [isTourOpen, setIsTourOpen] = useState(false);
  // "Don't show this again" — mirrors the stored opt-out so re-opening the
  // guide shows the current setting, and unchecking it re-enables auto-show.
  const [tourOptOut, setTourOptOut] = useState(
    () => !!localStorage.getItem('pccTourOptOut'),
  );
  // Welcome-step preset picker stage: 'root' (B2C vs B2B2C) or 'b2b2c'
  // (fork: where is the content published).
  const [tourAudience, setTourAudience] = useState(null);
  const [isSponsorshipCalendarVisible, setIsSponsorshipCalendarVisible] =
    useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [tablePageSize, setTablePageSize] = useState(15);

  const [selectedRowData, setSelectedRowData] = useState(null);
  const category = watch('category', []);
  const isPtrSwitchOn = watch('ptr');
  const isMastercardCollectionSelected = watch('mastercardCollection');
  const mcProgramSelection = watch('mcProgramSelection');
  const selectedImplementationTypes = watch('calculatedImplementationTypes');
  const isB2BImplementationSelected =
    Array.isArray(selectedImplementationTypes) &&
    selectedImplementationTypes.some(t => t.startsWith('B2B'));

  const formValues = watch();

  // Active-selection counts per collapse section, shown as header badges so
  // applied filters stay visible while a section is collapsed. Counts each
  // selected value (arrays) or set control (dates/booleans/checkboxes) — the
  // badge always mirrors what is visibly selected inside the section. (The
  // default-on aggregator exclusion therefore shows as "1" on landing: an
  // exclusion genuinely is active by default.)
  const countOf = v => (Array.isArray(v) ? v.length : v ? 1 : 0);

  // Explicitly searching for Benefits overrides the default-on exclusion
  // (backend enforces the same precedence); the checkbox is disabled and the
  // badge doesn't count it while it's being ignored.
  const contentTypeIncludesBenefits =
    Array.isArray(formValues.contentType) &&
    formValues.contentType.includes('Benefit');
  const panelSelectionCounts = {
    availability:
      countOf(formValues.contentStatus) +
      // The availability range counts as one selection however many ends are set.
      (Array.isArray(formValues.availabilityRange) &&
      formValues.availabilityRange.some(Boolean)
        ? 1
        : 0) +
      countOf(formValues.activationMonth),
    location:
      countOf(formValues.countries) + countOf(formValues.globalAvailability),
    content:
      countOf(formValues.category) +
      countOf(formValues.sports) +
      countOf(formValues.subcategories) +
      countOf(formValues.contentType) +
      countOf(formValues.redemptionType) +
      countOf(formValues.priceGroup) +
      countOf(formValues.languages),
    implementation:
      countOf(formValues.calculatedImplementationTypes) +
      countOf(formValues.ptr) +
      countOf(formValues.ptr2) +
      countOf(formValues.cardTypeNames),
    programs:
      countOf(formValues.programNames) +
      countOf(formValues.aggregators) +
      (formValues.mastercardCollection ? 1 : 0) +
      countOf(formValues.mastercardCollectionTypes) +
      countOf(formValues.linkedSponsorship) +
      (formValues.ecoFriendly ? 1 : 0) +
      (formValues.epic ? 1 : 0) +
      (formValues.isIconic ? 1 : 0) +
      (formValues.privilegedAccess ? 1 : 0) +
      (formValues.specialTreat ? 1 : 0),
    exclusions:
      (formValues.exclude_mastercard_collection &&
      !isMastercardCollectionSelected
        ? 1
        : 0) +
      (formValues.exclude_content_aggregators ? 1 : 0) +
      (formValues.exclude_benefits && !contentTypeIncludesBenefits ? 1 : 0) +
      (formValues.exclude_travel_benefits ? 1 : 0) +
      countOf(formValues.excludeProgramNames),
  };

  // Include/exclude program lists are mutually exclusive: each dropdown omits
  // the other's selections, so a program can't be both required and excluded.
  const includableProgramOptions = programs.filter(
    p => !(formValues.excludeProgramNames ?? []).includes(p.value),
  );
  const excludableProgramOptions = programs.filter(
    p => !(formValues.programNames ?? []).includes(p.value),
  );

  // Options for the nested Mastercard Collection program tree, derived from
  // the server facet: (category, programName) pairs present in the current MC
  // result set, where category is one of the collection's own categories
  // (Travel / Entertainment / Dining, from its subcategory tags). No hardcoded
  // program list — new programs tagged into the collection show up
  // automatically.
  // Memoized: the tree must receive stable treeData/value identities, or its
  // internal motion effects re-fire on every render and loop ("Maximum update
  // depth exceeded").
  const mcProgramPairs = useMemo(
    () => summary?.mc_program_category_pairs ?? [],
    [summary],
  );
  const mcProgramTreeData = useMemo(
    () =>
      Object.values(
        mcProgramPairs.reduce((acc, pair) => {
          if (!acc[pair.category]) {
            acc[pair.category] = {
              title: pair.category,
              key: pair.category,
              children: [],
            };
          }
          acc[pair.category].children.push({
            title: `${pair.programName} (${pair.count})`,
            key: `${pair.category}|||${pair.programName}`,
          });
          return acc;
        }, {}),
      )
        .map(cat => ({
          ...cat,
          children: [...cat.children].sort((a, b) =>
            a.title.localeCompare(b.title),
          ),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [mcProgramPairs],
  );

  // Every key (categories + programs) — the default "all selected" state.
  const mcAllTreeKeys = useMemo(
    () =>
      mcProgramTreeData.flatMap(cat => [
        cat.key,
        ...cat.children.map(child => child.key),
      ]),
    [mcProgramTreeData],
  );

  // Checked leaf keys ("cat|||program") → explicit pairs for the backend.
  // (antd Tree's onCheck reports every checked node, so leaves are always
  // present — parents don't need expanding.)
  const expandMcSelection = selection =>
    (selection ?? [])
      .filter(key => key.includes('|||'))
      .map(key => {
        const sepIndex = key.indexOf('|||');
        return {
          category: key.slice(0, sepIndex),
          programName: key.slice(sepIndex + 3),
        };
      });

  // The dining sub-filters belong to the collection's Dining category; hide
  // them when Dining has been deselected in the tree. (No selection yet =
  // default all-selected = Dining included.)
  const isDiningSelectedInMcTree =
    !mcProgramSelection ||
    mcProgramSelection.some(v => v === 'Dining' || v.startsWith('Dining|||'));

  // Likewise for the Entertainment ticket-type sub-filters.
  const isEntertainmentSelectedInMcTree =
    !mcProgramSelection ||
    mcProgramSelection.some(
      v => v === 'Entertainment' || v.startsWith('Entertainment|||'),
    );

  // Coming Soon content has no language data, so the backend only applies the
  // language filter to live rows — coming-soon results pass through regardless.
  // Warn when a language is set AND the results actually contain coming-soon
  // content. We use the server-computed summary count (calculated over the full
  // match set, not the client page) so this stays correct under either
  // client-side or future server-side pagination.
  const languageFilterActive = (formValues.languages?.length ?? 0) > 0;
  const hasComingSoonResults = (summary?.coming_soon_count ?? 0) > 0;
  const showComingSoonLanguageNotice =
    languageFilterActive && hasComingSoonResults;

  const [showTable, setShowTable] = useState(false);
  const [sortKey, setSortKey] = useState('passionCategory'); // default sort

  // Depend on stable booleans, not the watched array: watch('category', [])
  // returns a NEW [] identity every render while unset, which made this effect
  // fire (and unregister → notify → re-render) in an infinite loop.
  const categoryIncludesSports =
    Array.isArray(category) && category.includes('Sports');

  useEffect(() => {
    if (!categoryIncludesSports) {
      unregister('sports'); // This will unregister and clear the value of the 'sports' field
    }
  }, [categoryIncludesSports, unregister]);

  // Clear the dependent program tree + dining sub-filters when "Mastercard
  // Collection" is unchecked (the sub-options are also hidden in that case).
  useEffect(() => {
    if (!isMastercardCollectionSelected) {
      unregister('mastercardCollectionTypes');
      unregister('mcProgramSelection');
    }
  }, [isMastercardCollectionSelected, unregister]);

  // Pass-through rights only apply to B2B content; the PTR controls are hidden
  // unless a B2B implementation type is selected, so clear them when hidden.
  useEffect(() => {
    if (!isB2BImplementationSelected) {
      unregister('ptr');
      unregister('ptr2');
    }
  }, [isB2BImplementationSelected, unregister]);

  // ---- First-run guided tour (FRE) ---------------------------------------
  // Shown once per browser session (sessionStorage flag, per client request)
  // unless the user opted out permanently via "Don't show this again"
  // (localStorage); always replayable via the "Guide" control in the Filters
  // header. The preset cards map the client's use cases onto the existing
  // filters — they only set values, never a separate mode.
  useEffect(() => {
    if (
      !sessionStorage.getItem('pccTourSeen') &&
      !localStorage.getItem('pccTourOptOut')
    ) {
      setIsTourOpen(true);
    }
  }, []);

  const dismissTour = () => {
    sessionStorage.setItem('pccTourSeen', '1');
    if (tourOptOut) {
      localStorage.setItem('pccTourOptOut', '1');
    } else {
      localStorage.removeItem('pccTourOptOut');
    }
    setIsTourOpen(false);
    setTourAudience(null);
  };

  // Applies audience (from tourAudience state) × destination. Presets are
  // starting points, not additive tweaks: reset every field a preset can
  // govern back to its default first, so picking one preset after another
  // (or after manual fiddling) can't leave stale state behind. Orthogonal
  // filters (regions, dates, languages…) are left alone. Setting
  // mastercardCollection false also clears its dependent tree and dining
  // sub-filters via the existing clear-on-hide effect.
  const applyTourPreset = destination => {
    setValue('calculatedImplementationTypes', []);
    setValue('mastercardCollection', false);
    setValue('exclude_mastercard_collection', false);
    setValue('exclude_content_aggregators', 'EXCLUDE_CONTENT_AGGREGATORS');
    setValue('ptr', false);
    setValue('ptr2', []);

    const openPanels = ['implementation'];

    if (tourAudience === 'b2c') {
      setValue('calculatedImplementationTypes', ['B2C']);
    } else {
      setValue('calculatedImplementationTypes', [
        'B2B (general)',
        'B2B (partner specific)',
      ]);
      setValue('exclude_content_aggregators', false);
      // B2B2C content is redistributed by partners, so it must carry
      // pass-through rights — preselect the PTR toggle.
      setValue('ptr', 'PTR_ALLOWED');
    }

    if (destination === 'tmc') {
      setValue('mastercardCollection', true);
      openPanels.push('programs');
    } else {
      // "Published on the Priceless.com side" = not the Collection — the
      // mirror of the TMC destination, which narrows TO the Collection.
      setValue(
        'exclude_mastercard_collection',
        'EXCLUDE_MASTERCARD_COLLECTION',
      );
      openPanels.push('exclusions');
    }

    setActiveFilterPanels(openPanels);
    dismissTour();
  };

  const presetCardClass =
    'block w-full rounded-md border border-gray-200 bg-white p-3 text-left hover:border-orange-400 hover:bg-orange-50';

  const tourSteps = [
    {
      title: 'Welcome to the Content Catalog',
      description: (
        <div className="mcfont space-y-2 pt-1">
          {tourAudience === null ? (
            <>
              <p className="text-sm text-gray-600">
                Pick a starting point or tap the{' '}
                <span className="font-semibold text-[#CF4500]">Start Tour</span>{' '}
                button to take a quick tour.
              </p>
              <button
                type="button"
                className={presetCardClass}
                onClick={() => setTourAudience('b2c')}>
                <span className="block text-sm font-semibold text-gray-900">
                  B2C Content
                </span>
                <span className="block text-sm text-gray-500">
                  Content available directly on priceless.com that cardholders
                  can browse and book.
                </span>
              </button>
              <button
                type="button"
                className={presetCardClass}
                onClick={() => setTourAudience('b2b2c')}>
                <span className="block text-sm font-semibold text-gray-900">
                  B2B2C Content
                </span>
                <span className="block text-sm text-gray-500">
                  Content made available to customers through
                  Mastercard-integrated partner programs.
                </span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setTourAudience(null)}
                className="text-xs font-semibold text-gray-500 hover:text-orange-700">
                &larr; Back
              </button>
              <p className="text-sm font-semibold text-gray-800">
                Select where the content will be published:
              </p>
              <button
                type="button"
                className={presetCardClass}
                onClick={() => applyTourPreset('tmc')}>
                <span className="block text-sm font-semibold text-gray-900">
                  The Mastercard Collection
                </span>
                <span className="block text-sm text-gray-500">
                  Content available on The Mastercard Collection side.
                </span>
              </button>
              <button
                type="button"
                className={presetCardClass}
                onClick={() => applyTourPreset('priceless')}>
                <span className="block text-sm font-semibold text-gray-900">
                  Priceless.com
                </span>
                <span className="block text-sm text-gray-500">
                  Content available on priceless.com side.
                </span>
              </button>
            </>
          )}
          <label className="flex cursor-pointer items-center gap-2 pt-1 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={tourOptOut}
              onChange={e => setTourOptOut(e.target.checked)}
              className="size-3.5 rounded-sm border-gray-300 accent-orange-500"
            />
            Don&apos;t show this again on future visits
          </label>
        </div>
      ),
      nextButtonProps: { children: 'Start Tour' },
    },
    {
      title: 'Filters',
      description:
        'Search by text, or expand the sections below it — every filter stacks with the others.',
      target: () => document.getElementById('tour-filter-text'),
    },
    {
      title: 'B2C or B2B2C?',
      description:
        'B2C is cardholder content on priceless.com. B2B2C covers content that can be distributed through Mastercard-integrated customers — along with pass-through rights and card type.',
      target: () => document.getElementById('tour-eligibility'),
    },
    {
      title: 'Programs & The Mastercard Collection',
      description:
        'Filter by program, or check "The Mastercard Collection" to browse the collection by category and program.',
      target: () => document.getElementById('tour-programs'),
    },
    {
      title: 'Results & export',
      description:
        'The catalog updates live as you filter — sort here, view a summary, and export as PDF or CSV.',
      target: () => document.getElementById('tour-results'),
    },
  ];

  // Open the relevant sidebar section as its tour step activates, so the
  // spotlighted header has its content visible underneath.
  const handleTourStepChange = current => {
    if (current === 2) {
      setActiveFilterPanels(prev =>
        prev.includes('implementation') ? prev : [...prev, 'implementation'],
      );
    } else if (current === 3) {
      setActiveFilterPanels(prev =>
        prev.includes('programs') ? prev : [...prev, 'programs'],
      );
    }
  };

  const debouncedFetchData = debounce(() => {
    fetchData(formValues);
  }, 300);

  useDeepCompareEffect(() => {
    debouncedFetchData();

    return () => debouncedFetchData.cancel();
  }, [formValues]);

  const fetchData = async () => {
    console.log('fetchData was called');
    setLoading(true);

    const regionToCountries = Object.fromEntries(
      countriesByRegion.map(region => [
        region.value,
        region.children.map(country => country.value),
      ]),
    );

    // Replace any region names with all of the countries in that region.
    let countries = formValues.countries
      ? formValues.countries.flatMap(
          countryOrRegion =>
            regionToCountries[countryOrRegion] || countryOrRegion,
        )
      : [];

    let globalAvailability = formValues.globalAvailability;
    if (globalAvailability && globalAvailability.length === 1) {
      let val = globalAvailability[0];
      if (val === 'Available Globally') {
        globalAvailability = true;
      } else {
        globalAvailability = false;
      }
    } else {
      globalAvailability = undefined;
    }

    let activationMonth = formValues.activationMonth
      ? dayjs(formValues.activationMonth).format('YYYY-MM')
      : null;

    console.log(
      'calculatedImplementationTypes: ',
      formValues.calculatedImplementationTypes,
    );
    console.log('ptr1: ', formValues.ptr);
    console.log('ptr2: ', formValues.ptr2);

    console.log(
      'exclude_mastercard_collection value: ',
      formValues.exclude_mastercard_collection,
    );

    let shouldExcludeMastercardCollection =
      formValues.exclude_mastercard_collection ===
      'EXCLUDE_MASTERCARD_COLLECTION';
    let shouldExcludeContentAggregators =
      formValues.exclude_content_aggregators === 'EXCLUDE_CONTENT_AGGREGATORS';
    let shouldExcludeBenefits =
      formValues.exclude_benefits === 'EXCLUDE_BENEFITS';
    let shouldExcludeTravelBenefits =
      formValues.exclude_travel_benefits === 'EXCLUDE_TRAVEL_BENEFITS';

    // Nested MC program tree → explicit pairs. Omitted while untouched or when
    // every pair is still selected, so the default state adds no constraint.
    let mcSelectedProgramPairs;
    if (formValues.mastercardCollection && formValues.mcProgramSelection) {
      const pairs = expandMcSelection(formValues.mcProgramSelection);
      if (pairs.length < mcProgramPairs.length) {
        mcSelectedProgramPairs = pairs;
      }
    }

    let payload = {
      category: formValues.category,
      sports: formValues.sports,
      contentStatus: formValues.contentStatus,
      contentType: formValues.contentType,
      countries: countries,
      globalAvailability: globalAvailability,
      availableFrom: formValues.availabilityRange?.[0]
        ? dayjs(formValues.availabilityRange[0]).startOf('day').toISOString()
        : null,
      availableUntil: formValues.availabilityRange?.[1]
        ? dayjs(formValues.availabilityRange[1]).endOf('day').toISOString()
        : null,
      activationMonth: activationMonth,
      priceGroup: formValues.priceGroup,
      redemptionType: formValues.redemptionType,
      subcategories: formValues.subcategories,
      linkedSponsorship: formValues.linkedSponsorship,
      programNames: formValues.programNames,
      cardTypeNames: formValues.cardTypeNames,
      aggregators: formValues.aggregators,
      mastercardCollection: formValues.mastercardCollection,
      mastercardCollectionTypes: formValues.mastercardCollectionTypes,
      mastercardProgramPairs: mcSelectedProgramPairs,
      eitherPtrRequired: formValues.ptr,
      passThroughRights: formValues.ptr2 || [],
      calculatedImplementationTypes: formValues.calculatedImplementationTypes,
      languages: formValues.languages,
      specialCategories: getSpecialCategories(),
      excludeProgramNames: formValues.excludeProgramNames ?? [],
      excludeMastercardCollection: shouldExcludeMastercardCollection,
      excludeContentAggregators: shouldExcludeContentAggregators,
      excludeBenefits: shouldExcludeBenefits,
      excludeTravelBenefits: shouldExcludeTravelBenefits,
    };

    console.log('Payload: ', payload);
    const { data: responseData, error } = await supabase.functions.invoke(
      'products-search-v3',
      {
        body: payload,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      },
    );

    if (error) {
      console.error('Error fetching data:', error);
    } else if (responseData.error) {
      console.error('Error fetching data row:', responseData.error);
    } else {
      // console.log("Successfully fetched data:");
      // console.log(responseData.summary);
      setData(responseData.data);
      setSummary(responseData.summary);
    }
    setLoading(false);
  };

  const generateAndStorePDF = async () => {
    try {
      setPdfLoading(true);

      const doc = (
        <ProductPDFDocument
          groupedProducts={groupedDataBySortKey(undefined)}
          user={user}
          searchParams={listOfFiltersFromFormData()}
          summary={summary}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `priceless_${dayjs().format('YYYY-MM-DDTHH:mm')}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(link);
    } catch (e) {
      console.error('Error generating PDF:', e);

      let log = JSON.stringify({
        error: e,
      });

      supabase.functions.invoke('pipeline-log', {
        body: { event: 'PDF ERROR', body: log },
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      });

      alert('Error generating product PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const sortFunctions = {
    passionCategory: (a, b) => {
      if (a.passionCategory === null) return 1;
      if (b.passionCategory === null) return -1;
      return a.passionCategory.localeCompare(b.passionCategory);
    },
    region: (a, b) => {
      if (a.region === null) return 1;
      if (b.region === null) return -1;
      return a.region.localeCompare(b.region);
    },
    contentType: (a, b) => {
      if (a.contentType === null) return 1;
      if (b.contentType === null) return -1;
      return a.contentType.localeCompare(b.contentType);
    },
    redemptionType: (a, b) => {
      if (a.redemptionType === null) return 1;
      if (b.redemptionType === null) return -1;
      return a.redemptionType.localeCompare(b.redemptionType);
    },
    'Price (low-to-high)': (a, b) => {
      const priceA = a.price != null ? a.price : -Infinity;
      const priceB = b.price != null ? b.price : -Infinity;
      return priceA - priceB;
    },
    'Price (high-to-low)': (a, b) => {
      const priceA = a.price != null ? a.price : -Infinity;
      const priceB = b.price != null ? b.price : -Infinity;
      return priceB - priceA;
    },
  };

  const flattenGroupedData = groupedData => {
    const result = [];

    Object.keys(groupedData).forEach(groupTitle => {
      if (groupTitle.length > 0) {
        result.push({ isGroupTitle: true, groupTitle });
      }
      groupedData[groupTitle].forEach(item => {
        result.push(item);
      });
    });

    return result;
  };

  const downloadCSV = () => {
    const csvData = convertToCSV(data);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    const date = dayjs().format('YYYY-MM-DDTHH:mm');
    a.download = `priceless_content_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stringSorter = key => (a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    if (!valueA && !valueB) return 0; // Both are null/undefined
    if (!valueA) return -1; // Only a is null/undefined
    if (!valueB) return 1; // Only b is null/undefined

    return String(valueA).localeCompare(String(valueB));
  };

  const handleTableChange = pagination => {
    setTablePageSize(pagination.pageSize);
  };

  const columns = [
    {
      title: 'id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      render: (id, record) => (record.status === 'coming_soon' ? 'N/A' : id),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      sorter: stringSorter('title'),
      render: text => (text ? cleanString(text) : '-'),
    },
    {
      title: 'Merchant',
      dataIndex: 'merchant',
      sorter: stringSorter('merchant'),
      render: text => text || '-',
    },
    {
      title: 'Type',
      dataIndex: 'contentType',
      sorter: stringSorter('contentType'),
      render: text => text || '-',
      filters: [
        {
          text: 'In-Person Experience',
          value: 'In-Person Experience',
        },
        {
          text: 'Digital Experience',
          value: 'Digital Experience',
        },
        {
          text: 'Editorial Content',
          value: 'Editorial Content',
        },
        {
          text: 'Offer',
          value: 'Offer',
        },
        {
          text: 'Physical Product',
          value: 'Physical Product',
        },
      ],
      onFilter: (value, record) => {
        if (!record.contentType) {
          return false;
        }
        return record.contentType.localeCompare(value) === 0;
      },
    },
    {
      title: 'Category',
      dataIndex: 'passionCategory',
      sorter: stringSorter('passionCategory'),
      render: text => text || '-',
      filters: [
        {
          text: 'Arts & Culture',
          value: 'Arts & Culture',
        },
        {
          text: 'Culinary',
          value: 'Culinary',
        },
        {
          text: 'Entertainment',
          value: 'Entertainment',
        },
        {
          text: 'Fashion and Shopping',
          value: 'Fashion and Shopping',
        },
        {
          text: 'Sports',
          value: 'Sports',
        },
        {
          text: 'Travel',
          value: 'Travel',
        },
        {
          text: 'Health and Wellness',
          value: 'Health and Wellness',
        },
      ],
      onFilter: (value, record) => {
        if (!record.passionCategory) {
          return false;
        }
        return record.passionCategory.localeCompare(value) === 0;
      },
    },
    {
      title: 'Country',
      dataIndex: 'country',
      sorter: stringSorter('country'),
      render: text => text || '-',
    },
  ];

  const handleShowSummary = () => {
    setIsSummaryModalVisible(true);
  };

  const handleSummaryModalClose = () => {
    setIsSummaryModalVisible(false);
  };

  const handleShowDetails = record => {
    setSelectedRowData(record);
    setProductInfoVisible(true);
  };

  const handleModalClose = () => {
    setProductInfoVisible(false);
  };

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const filteredData = [...data]
    .sort(sortFunctions[sortKey])
    .filter(item =>
      Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );

  const getSpecialCategories = () => {
    const specialCategories = [
      formValues.isIconic ? specialContentTypeStrings.iconic : null,
      formValues.ecoFriendly ? specialContentTypeStrings.ecoFriendly : null,
      formValues.epic ? specialContentTypeStrings.epic : null,
      formValues.privilegedAccess
        ? specialContentTypeStrings.privilegedAccess
        : null,
      formValues.specialTreat ? specialContentTypeStrings.specialTreat : null,
    ].filter(Boolean);
    return specialCategories;
  };

  const listOfFiltersFromFormData = () => {
    let paramObject = formValues;
    paramObject = Object.fromEntries(
      Object.entries(paramObject).filter(([_, value]) => value !== undefined),
    );

    if (paramObject.ecoFriendly === true) {
      paramObject.ecoFriendly = 'Eco-friendly';
    } else {
      paramObject.ecoFriendly = '';
    }

    if (paramObject.isIconic === true) {
      paramObject.isIconic = 'Iconic';
    } else {
      paramObject.isIconic = '';
    }

    if (paramObject.contentStatus === false) {
      paramObject.contentStatus = '';
    }

    if (paramObject.priceGroup === false) {
      paramObject.priceGroup = '';
    }

    if (paramObject.ptr === false) {
      paramObject.ptr = '';
    }

    if (paramObject.ptr2 === false) {
      paramObject.ptr2 = '';
    }

    if (paramObject.globalAvailability === false) {
      paramObject.globalAvailability = '';
    }

    if (paramObject.epic === true) {
      paramObject.epic = 'Epic';
    } else {
      paramObject.epic = '';
    }

    if (paramObject.privilegedAccess === true) {
      paramObject.privilegedAccess = 'Privileged Access';
    } else {
      paramObject.privilegedAccess = '';
    }

    if (paramObject.specialTreat === true) {
      paramObject.specialTreat = 'Special Treat';
    } else {
      paramObject.specialTreat = '';
    }

    // Exclusions → readable summary entries instead of raw form tokens.
    // Precedence-ignored exclusions (MC selected / Benefits in Content Type)
    // are dropped, mirroring the badge counts.
    paramObject.excludeProgramNames = Array.isArray(
      paramObject.excludeProgramNames,
    )
      ? paramObject.excludeProgramNames.map(name => `Excluding ${name}`)
      : '';
    paramObject.exclude_mastercard_collection =
      paramObject.exclude_mastercard_collection &&
      !isMastercardCollectionSelected
        ? 'Excluding Mastercard Collection'
        : '';
    paramObject.exclude_content_aggregators =
      paramObject.exclude_content_aggregators
        ? 'Excluding Content from Aggregator Partners'
        : '';
    paramObject.exclude_benefits =
      paramObject.exclude_benefits && !contentTypeIncludesBenefits
        ? 'Excluding Benefits'
        : '';
    paramObject.exclude_travel_benefits = paramObject.exclude_travel_benefits
      ? 'Excluding Travel Benefits (MTR)'
      : '';

    // Availability range → one readable string for the summary / PDFs.
    {
      const [from, until] = Array.isArray(paramObject.availabilityRange)
        ? paramObject.availabilityRange
        : [null, null];
      const fmt = d => formatDateMedium(dayjs(d));
      if (from && until) {
        paramObject.availabilityRange = `Available ${fmt(from)} – ${fmt(until)}`;
      } else if (from) {
        paramObject.availabilityRange = `Available from ${fmt(from)}`;
      } else if (until) {
        paramObject.availabilityRange = `Available until ${fmt(until)}`;
      } else {
        paramObject.availabilityRange = '';
      }
    }

    if (paramObject.activationMonth) {
      paramObject.activationMonth = dayjs(paramObject.activationMonth).format(
        'MMMM YYYY',
      );
    }

    return paramObject;
  };

  const groupedData = [
    'passionCategory',
    'region',
    'contentType',
    'redemptionType',
  ].includes(sortKey)
    ? filteredData.reduce((groups, item) => {
        const group =
          sortKey === 'region' && item[sortKey]
            ? regionMapping[item[sortKey]]
            : item[sortKey] || 'Other';

        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(item);
        return groups;
      }, {})
    : { '': filteredData };

  function groupedDataBySortKey(n) {
    // Consider only the first n elements of filteredData
    const slicedData = filteredData.slice(0, n);

    if (
      ['passionCategory', 'region', 'contentType', 'redemptionType'].includes(
        sortKey,
      )
    ) {
      return slicedData.reduce((groups, item) => {
        const group =
          sortKey === 'region' && item[sortKey]
            ? regionMapping[item[sortKey]]
            : item[sortKey] || 'Other';

        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(item);
        return groups;
      }, {});
    } else {
      // Price sort, will be flat (only one group)
      return { [`Sorted by: ${sortKey}`]: slicedData };
    }
  }

  const resetFilters = () => {
    setSearchTerm('');
    setSearchTerm('');
    reset(emptyState);
  };

  // console.log("groupedData is", groupedData);
  // console.log("flattenGroupedData is", flattenGroupedData(groupedData));

  return (
    <div className="bg-gray-100 min-h-screen">
      <title>Catalog | Priceless Content Catalog</title>

      <header className="bg-white shadow border-gray-200 ">
        <div className="w-full px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold tracking-tight mcfont text-gray-900">
            Content Catalog
          </h1>
        </div>
      </header>

      <main>
        <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
          {showComingSoonLanguageNotice && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3">
              <InformationCircleIcon
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-700"
                aria-hidden="true"
              />
              <p className="mcfont text-sm text-orange-900">
                Coming Soon content has no language data yet, so it's shown
                regardless of your selected language(s) — those results may not
                match your language filter.
              </p>
            </div>
          )}
          <div
            id="tour-results"
            className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="flex items-center whitespace-nowrap text-md text-orange-700 font-semibold mcfont">
              {loading
                ? 'Searching…'
                : `${filteredData.length.toLocaleString()} results found`}
              <Tooltip
                placement="bottom"
                title={
                  <div style={{ textAlign: 'center' }}>
                    Benefits, Travel Benefits (MTR), and content providers are
                    excluded by default — manage this under Filters &rarr;
                    Exclusions.
                  </div>
                }>
                <InformationCircleIcon
                  className="ml-1 mr-1 h-6 w-6 "
                  aria-hidden="true"
                />
              </Tooltip>
            </p>

            <div className="flex flex-wrap items-center gap-3">
              {data.length > 0 && !showTable && (
                <div className="flex items-center mcfont">
                  <label
                    htmlFor="sort-select"
                    className="mr-2 whitespace-nowrap text-sm font-semibold text-gray-700">
                    Sort by:
                  </label>
                  <div className="grid grid-cols-1">
                    <select
                      id="sort-select"
                      className="col-start-1 w-full row-start-1 font-semibold appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-400 sm:text-sm/6"
                      onChange={e => setSortKey(e.target.value)}
                      value={sortKey}>
                      <option value="passionCategory">Passion Category</option>
                      <option value="region">Region</option>
                      <option value="contentType">Content Type</option>
                      <option value="redemptionType">Redemption Type</option>
                      <option value="Price (low-to-high)">
                        Price (low-to-high)
                      </option>
                      <option value="Price (high-to-low)">
                        Price (high-to-low)
                      </option>{' '}
                    </select>
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleShowSummary}
                className="inline-flex mcfont items-center gap-x-1.5 whitespace-nowrap rounded-md bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 shadow-sm hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Catalog Summary
                <ChevronDownIcon
                  className="-mr-0.5 h-5 w-5"
                  aria-hidden="true"
                />
              </button>

              {/* Only display export PDF if user has export rights */}
              {(isAdmin(user) || hasExportPrivileges(user)) &&
                data.length > 0 && (
                  <button
                    type="button"
                    onClick={generateAndStorePDF}
                    disabled={pdfLoading}
                    className={`inline-flex mcfont items-center gap-x-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold shadow-sm hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                      pdfLoading
                        ? 'bg-orange-50 text-orange-600'
                        : 'bg-orange-50 text-orange-600'
                    }`}>
                    <DocumentChartBarIcon
                      className="-ml-0.5 h-5 w-5"
                      aria-hidden="true"
                    />
                    {pdfLoading ? 'Generating PDF...' : 'Export as PDF'}
                  </button>
                )}

              {/* Only display download CSV if user has export rights */}
              {(isAdmin(user) || hasExportPrivileges(user)) &&
                data.length > 0 && (
                  <button
                    type="button"
                    onClick={downloadCSV}
                    className="inline-flex mcfont items-center gap-x-1.5 whitespace-nowrap rounded-md bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 shadow-sm hover:bg-orange-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                    <ArrowDownTrayIcon
                      className="-ml-0.5 h-5 w-5"
                      aria-hidden="true"
                    />
                    Download as CSV
                  </button>
                )}
            </div>
          </div>

          <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
            <div className="mcfont w-full shrink-0 xl:w-[320px]">
              <div className="mb-3 flex items-center justify-between px-1">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => setIsTourOpen(true)}
                    className="inline-flex items-center gap-x-1 rounded-md bg-orange-50 px-2.5 py-1.5 text-sm font-semibold text-orange-600 shadow-xs hover:bg-orange-100">
                    <QuestionMarkCircleIcon
                      className="h-4 w-4"
                      aria-hidden="true"
                    />
                    Guide
                  </button>
                  {formValues !== emptyState && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="text-sm underline font-semibold text-[#CF4500] hover:text-orange-800">
                      Clear all
                    </button>
                  )}
                </div>
              </div>

              <div
                id="tour-filter-text"
                className="mb-3 rounded-lg border border-gray-200 bg-white p-3.5 shadow-xs">
                <label
                  htmlFor="about"
                  className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                  Filter by Text
                </label>
                <Input
                  placeholder="Title, description, merchant"
                  onChange={handleSearch}
                  allowClear
                  value={searchTerm}
                  prefix={<SearchOutlined />}
                />
              </div>

              <form className="space-y-4">
                {/* // onSubmit={handleSubmit(onSubmit)} */}
                <Collapse
                  ghost
                  className="mc-filter-collapse"
                  activeKey={activeFilterPanels}
                  onChange={keys => setActiveFilterPanels(keys)}
                  items={[
                    {
                      key: 'availability',
                      forceRender: true,
                      label: (
                        <span className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Availability
                          <PanelBadge
                            count={panelSelectionCounts.availability}
                          />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Availability
                            </label>

                            <fieldset className="mt-2">
                              <div className="space-y-4 mb-1 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('contentStatus')}
                                      type="checkbox"
                                      id={'Coming Soon'}
                                      value={'Coming Soon'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'Coming Soon'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Coming Soon
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('contentStatus')}
                                      type="checkbox"
                                      id={'Live'}
                                      value={'Live'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'Live'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Live
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              When is the content available?
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    Find content that is live during a specific
                                    period.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <Controller
                              name="availabilityRange"
                              control={control}
                              render={({ field }) => (
                                <DatePicker.RangePicker
                                  {...field}
                                  className="w-full"
                                  value={field.value}
                                  onChange={values => field.onChange(values)}
                                  format={localeDateFormat}
                                  placeholder={[
                                    'Available from',
                                    'Available until',
                                  ]}
                                  allowEmpty={[true, true]}
                                />
                              )}
                            />
                          </div>
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Activation Month
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    Show content whose go-live date falls within
                                    the selected month.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <Controller
                              name="activationMonth"
                              control={control}
                              render={({ field }) => (
                                <DatePicker
                                  {...field}
                                  defaultValue={null}
                                  className="w-full"
                                  value={field.value}
                                  onChange={values => field.onChange(values)}
                                  picker="month"
                                  format="MMMM YYYY"
                                />
                              )}
                            />
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'location',
                      forceRender: true,
                      label: (
                        <span className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Location &amp; Markets
                          <PanelBadge count={panelSelectionCounts.location} />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Regions / Markets
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    Select one or more regions/markets where the
                                    content was created.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="">
                              <Controller
                                name="countries"
                                control={control}
                                render={({ field }) => (
                                  <TreeSelect
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    treeData={countriesByRegion}
                                    allowClear
                                    treeCheckable={true}
                                    showCheckedStrategy={SHOW_PARENT}
                                    style={{ width: '100%' }}
                                    placeholder="Select markets..."
                                    onChange={values => field.onChange(values)}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Geographic Availability
                            </label>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div
                                  key={'Available Globally'}
                                  className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('globalAvailability')}
                                      type="checkbox"
                                      id={'Available Globally'}
                                      value={'Available Globally'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'Available Globally'}
                                    className={classNames(
                                      'text-gray-700',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Global
                                  </label>

                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div style={{ textAlign: 'center' }}>
                                        Content that can be merchandized across
                                        multiple countries beyond its country of
                                        origin.
                                      </div>
                                    }>
                                    <InformationCircleIcon
                                      className="ml-1 mr-1 h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </Tooltip>
                                </div>

                                <div
                                  key={'Local Only'}
                                  className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('globalAvailability')}
                                      type="checkbox"
                                      id={'Local Only'}
                                      value={'Local'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>

                                  <label
                                    htmlFor={'Local Only'}
                                    className={classNames(
                                      'text-gray-700',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Local
                                  </label>

                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div style={{ textAlign: 'center' }}>
                                        Content that can be merchandized only in
                                        its origin country (for example, content
                                        created in Spain that can only be viewed
                                        in Spain).
                                      </div>
                                    }>
                                    <InformationCircleIcon
                                      className="ml-1 mr-1 h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </Tooltip>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'content',
                      forceRender: true,
                      label: (
                        <span className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Content
                          <PanelBadge count={panelSelectionCounts.content} />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Passion Category
                            </label>

                            <div className="mt-1">
                              <Controller
                                name="category"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    // size="middle"
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={categories}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          {category.includes('Sports') && (
                            <div className="col-span-full">
                              <label
                                htmlFor="about"
                                className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                                Sports
                              </label>

                              <div className="mt-1">
                                <Controller
                                  name="sports"
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      defaultValue={field.defaultValue}
                                      mode="multiple"
                                      allowClear
                                      style={{ width: '100%' }}
                                      // size="middle"
                                      placeholder="Select all that apply"
                                      onChange={values =>
                                        field.onChange(values)
                                      }
                                      options={sports}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          )}

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Subcategories
                            </label>

                            <div className="">
                              <Controller
                                name="subcategories"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={subcategories}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Content Type
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    The format of the content being offered.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="mt-1">
                              <Controller
                                name="contentType"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    // size="middle"
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={contentTypesCondensed}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Redemption Type
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    How users access, purchase, or redeem the
                                    content.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="">
                              <Controller
                                name="redemptionType"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={redemptionTypes}
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Price
                            </label>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('priceGroup')}
                                      type="checkbox"
                                      id={'Price-Free'}
                                      value={'Free'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'Price-Free'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Free
                                  </label>
                                </div>

                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('priceGroup')}
                                      type="checkbox"
                                      id={'Price-Paid'}
                                      value={'Paid'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>

                                  <label
                                    htmlFor={'Price-Paid'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    Paid
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Languages
                            </label>

                            <div className="">
                              <Controller
                                name="languages"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={languages}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'implementation',
                      forceRender: true,
                      label: (
                        <span
                          id="tour-eligibility"
                          className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Eligibility &amp; Usage
                          <PanelBadge
                            count={panelSelectionCounts.implementation}
                          />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Audience Type
                            </label>

                            <fieldset className="mt-2">
                              <div className="space-y-2">
                                {/* B2B (partner specific) content is admin-only; the
                            backend also enforces this. */}
                                {isAdmin(user) && (
                                  <div className="flex items-center">
                                    <div className="group grid size-4 grid-cols-1">
                                      <input
                                        {...register(
                                          'calculatedImplementationTypes',
                                        )}
                                        type="checkbox"
                                        id={'IX_B2B_PARTNER'}
                                        value={'B2B (partner specific)'}
                                        className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                      />
                                      <svg
                                        fill="none"
                                        viewBox="0 0 14 14"
                                        className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                        <path
                                          d="M3 8L6 11L11 3.5"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="opacity-0 group-has-checked:opacity-100"
                                        />
                                        <path
                                          d="M3 7H11"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="opacity-0 group-has-indeterminate:opacity-100"
                                        />
                                      </svg>
                                    </div>
                                    <label
                                      htmlFor={'IX_B2B_PARTNER'}
                                      className={classNames(
                                        'text-gray-800',
                                        'ml-3 block text-md font-medium leading-6 ',
                                      )}>
                                      B2B (partner specific)
                                    </label>

                                    <Tooltip
                                      placement="bottom"
                                      title={
                                        <div style={{ textAlign: 'center' }}>
                                          Content tied to specific Mastercard
                                          partners, sponsorships, or bespoke
                                          programs.
                                        </div>
                                      }>
                                      <InformationCircleIcon
                                        className="ml-1 mr-1 h-5 w-5 "
                                        aria-hidden="true"
                                      />
                                    </Tooltip>
                                  </div>
                                )}

                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register(
                                        'calculatedImplementationTypes',
                                      )}
                                      type="checkbox"
                                      id={'IX_B2B_GENERAL'}
                                      value={'B2B (general)'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'IX_B2B_GENERAL'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    B2B (general)
                                  </label>

                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div style={{ textAlign: 'center' }}>
                                        Broadly available B2B content, e.g.
                                        content aggregator programs.
                                      </div>
                                    }>
                                    <InformationCircleIcon
                                      className="ml-1 mr-1 h-5 w-5 "
                                      aria-hidden="true"
                                    />
                                  </Tooltip>
                                </div>

                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register(
                                        'calculatedImplementationTypes',
                                      )}
                                      type="checkbox"
                                      id={'IX_B2C'}
                                      value={'B2C'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'IX_B2C'}
                                    className={classNames(
                                      'text-gray-800',
                                      'ml-3 block text-md font-medium leading-6 ',
                                    )}>
                                    B2C
                                  </label>
                                  <Tooltip
                                    placement="bottom"
                                    title={
                                      <div style={{ textAlign: 'center' }}>
                                        Content available directly on
                                        priceless.com that cardholders can
                                        browse and book.
                                      </div>
                                    }>
                                    <InformationCircleIcon
                                      className="ml-1 mr-1 h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </Tooltip>
                                </div>
                              </div>
                            </fieldset>
                          </div>

                          {/* Pass-through rights only apply to B2B content; shown only while a
                      B2B implementation type is selected (values cleared when hidden). */}
                          {isB2BImplementationSelected && (
                            <div className="col-span-full">
                              <label
                                htmlFor="about"
                                className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                                Pass-Through Rights
                                <Tooltip
                                  placement="bottom"
                                  title={
                                    <div style={{ textAlign: 'center' }}>
                                      Customer distribution rights associated
                                      with the content, including whether it can
                                      be merchandised through co-brand websites,
                                      API integrations, or both.
                                    </div>
                                  }>
                                  <InformationCircleIcon
                                    className="ml-1 mr-1 h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </Tooltip>
                              </label>

                              <fieldset className="mt-2">
                                <div className="space-y-4 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                                  <div className="flex items-center">
                                    <div className="group grid size-4 grid-cols-1">
                                      <input
                                        {...register('ptr')}
                                        type="checkbox"
                                        id={'PTR_ALLOWED'}
                                        value={'PTR_ALLOWED'}
                                        className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                      />
                                      <svg
                                        fill="none"
                                        viewBox="0 0 14 14"
                                        className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                        <path
                                          d="M3 8L6 11L11 3.5"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="opacity-0 group-has-checked:opacity-100"
                                        />
                                        <path
                                          d="M3 7H11"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="opacity-0 group-has-indeterminate:opacity-100"
                                        />
                                      </svg>
                                    </div>
                                    <label
                                      htmlFor={'PTR_ALLOWED'}
                                      className={classNames(
                                        'text-gray-800',
                                        'ml-3 block text-md font-medium leading-6 ',
                                      )}>
                                      Has Pass-Through Rights
                                    </label>
                                  </div>
                                </div>
                              </fieldset>

                              {isPtrSwitchOn && (
                                <fieldset className="">
                                  <Divider
                                    plain
                                    style={{ margin: '10px 8px' }}
                                  />

                                  <div className="space-y-4 mt-3 mb-1 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                                    <div className="flex items-center">
                                      <div className="group grid size-4 grid-cols-1">
                                        <input
                                          {...register('ptr2')}
                                          type="checkbox"
                                          id={'PTR-API'}
                                          value={'PTR-API'}
                                          className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                        />
                                        <svg
                                          fill="none"
                                          viewBox="0 0 14 14"
                                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                          <path
                                            d="M3 8L6 11L11 3.5"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="opacity-0 group-has-checked:opacity-100"
                                          />
                                          <path
                                            d="M3 7H11"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="opacity-0 group-has-indeterminate:opacity-100"
                                          />
                                        </svg>
                                      </div>
                                      <label
                                        htmlFor={'PTR-API'}
                                        className={classNames(
                                          'text-gray-800',
                                          'ml-3 block text-md font-medium leading-6 ',
                                        )}>
                                        API
                                      </label>
                                    </div>

                                    <div className="flex items-center">
                                      <div className="group grid size-4 grid-cols-1">
                                        <input
                                          {...register('ptr2')}
                                          type="checkbox"
                                          id={'PTR-Cobrand'}
                                          value={'PTR-Cobrand'}
                                          className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                        />
                                        <svg
                                          fill="none"
                                          viewBox="0 0 14 14"
                                          className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                          <path
                                            d="M3 8L6 11L11 3.5"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="opacity-0 group-has-checked:opacity-100"
                                          />
                                          <path
                                            d="M3 7H11"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="opacity-0 group-has-indeterminate:opacity-100"
                                          />
                                        </svg>
                                      </div>

                                      <label
                                        htmlFor={'PTR-Cobrand'}
                                        className={classNames(
                                          'text-gray-800',
                                          'ml-3 block text-md font-medium leading-6 ',
                                        )}>
                                        Co-brand sites
                                      </label>
                                    </div>
                                  </div>
                                </fieldset>
                              )}
                            </div>
                          )}
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Card Type
                            </label>

                            <div className="">
                              <Controller
                                name="cardTypeNames"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={cardTypes}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'programs',
                      forceRender: true,
                      label: (
                        <span
                          id="tour-programs"
                          className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Programs &amp; Collections
                          <PanelBadge count={panelSelectionCounts.programs} />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Content Source Program
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    The program, platform, partner, or solution
                                    from which the content originates or is
                                    delivered.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="">
                              <Controller
                                name="programNames"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={includableProgramOptions}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-span-full">
                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('mastercardCollection')}
                                      type="checkbox"
                                      key={'mastercardCollection'}
                                      id={'mastercardCollection'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'mastercardCollection'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    The Mastercard Collection
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            {isMastercardCollectionSelected &&
                              mcProgramTreeData.length > 0 && (
                                <div className=" mt-3 rounded-md border border-gray-200">
                                  {/* Programs in the collection, grouped by its
                            categories (Travel / Entertainment / Dining).
                            Options come from the search response facet; all
                            selected (default) = no extra filter. */}
                                  <Controller
                                    name="mcProgramSelection"
                                    control={control}
                                    render={({ field }) => (
                                      <Tree
                                        className="mc-program-tree py-1"
                                        checkable
                                        selectable={false}
                                        defaultExpandAll
                                        blockNode
                                        treeData={mcProgramTreeData}
                                        // height={330}
                                        checkedKeys={
                                          field.value ?? mcAllTreeKeys
                                        }
                                        onCheck={checkedKeys =>
                                          field.onChange(checkedKeys)
                                        }
                                      />
                                    )}
                                  />

                                  {isDiningSelectedInMcTree && (
                                    <div className="space-y-2.5 border-t border-gray-100 px-3 py-2.5">
                                      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                        Dining Type
                                      </p>
                                      <div className="flex items-center">
                                        <div className="group grid size-4 grid-cols-1">
                                          <input
                                            {...register(
                                              'mastercardCollectionTypes',
                                            )}
                                            type="checkbox"
                                            id={'MC-DiningExperiences'}
                                            value={'Dining Experiences'}
                                            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                          />
                                          <svg
                                            fill="none"
                                            viewBox="0 0 14 14"
                                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                            <path
                                              d="M3 8L6 11L11 3.5"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-checked:opacity-100"
                                            />
                                            <path
                                              d="M3 7H11"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-indeterminate:opacity-100"
                                            />
                                          </svg>
                                        </div>
                                        <label
                                          htmlFor={'MC-DiningExperiences'}
                                          className="ml-3 block text-[13px] text-gray-900 font-medium leading-6">
                                          Dining Experiences
                                        </label>
                                      </div>

                                      <div className="flex items-center">
                                        <div className="group grid size-4 grid-cols-1">
                                          <input
                                            {...register(
                                              'mastercardCollectionTypes',
                                            )}
                                            type="checkbox"
                                            id={'MC-DiningHotspot'}
                                            value={'Dining Hotspot'}
                                            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                          />
                                          <svg
                                            fill="none"
                                            viewBox="0 0 14 14"
                                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                            <path
                                              d="M3 8L6 11L11 3.5"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-checked:opacity-100"
                                            />
                                            <path
                                              d="M3 7H11"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-indeterminate:opacity-100"
                                            />
                                          </svg>
                                        </div>
                                        <label
                                          htmlFor={'MC-DiningHotspot'}
                                          className="ml-3 block text-[13px] text-gray-900 font-medium leading-6">
                                          Dining Hotspot
                                        </label>
                                      </div>
                                    </div>
                                  )}

                                  {isEntertainmentSelectedInMcTree && (
                                    <div className="space-y-2.5 border-t border-gray-100 px-3 py-2.5">
                                      <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                                        Ticket Type
                                      </p>
                                      <div className="flex items-center">
                                        <div className="group grid size-4 grid-cols-1">
                                          <input
                                            {...register(
                                              'mastercardCollectionTypes',
                                            )}
                                            type="checkbox"
                                            id={'MC-PreferredTicket'}
                                            value={'Preferred Ticket'}
                                            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                          />
                                          <svg
                                            fill="none"
                                            viewBox="0 0 14 14"
                                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                            <path
                                              d="M3 8L6 11L11 3.5"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-checked:opacity-100"
                                            />
                                            <path
                                              d="M3 7H11"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-indeterminate:opacity-100"
                                            />
                                          </svg>
                                        </div>
                                        <label
                                          htmlFor={'MC-PreferredTicket'}
                                          className="ml-3 block text-[13px] text-gray-900 font-medium leading-6">
                                          Preferred Ticket
                                        </label>
                                      </div>

                                      <div className="flex items-center">
                                        <div className="group grid size-4 grid-cols-1">
                                          <input
                                            {...register(
                                              'mastercardCollectionTypes',
                                            )}
                                            type="checkbox"
                                            id={'MC-PresaleTicket'}
                                            value={'Presale Ticket'}
                                            className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                          />
                                          <svg
                                            fill="none"
                                            viewBox="0 0 14 14"
                                            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                            <path
                                              d="M3 8L6 11L11 3.5"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-checked:opacity-100"
                                            />
                                            <path
                                              d="M3 7H11"
                                              strokeWidth={2}
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="opacity-0 group-has-indeterminate:opacity-100"
                                            />
                                          </svg>
                                        </div>
                                        <label
                                          htmlFor={'MC-PresaleTicket'}
                                          className="ml-3 block text-[13px] text-gray-900 font-medium leading-6">
                                          Presale Ticket
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Content Aggregator
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    The external partner platform that supplies
                                    or sources the content.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="">
                              <Controller
                                name="aggregators"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => field.onChange(values)}
                                    options={aggregators}
                                  />
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 flex items-center text-sm font-semibold leading-6 text-gray-900">
                              Sponsorship Property
                              <Tooltip
                                placement="bottom"
                                title={
                                  <div style={{ textAlign: 'center' }}>
                                    The sponsorship program, event, or
                                    partnership associated with the content.
                                  </div>
                                }>
                                <InformationCircleIcon
                                  className="ml-1 mr-1 h-5 w-5"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            </label>

                            <div className="">
                              <Controller
                                name="linkedSponsorship"
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    defaultValue={field.defaultValue}
                                    mode="multiple"
                                    allowClear
                                    style={{ width: '100%' }}
                                    placeholder="Select all that apply"
                                    onChange={values => {
                                      if (values.includes('all')) {
                                        // Either select all or deselect all
                                        const allValuesSelected =
                                          sponsorships.every(s =>
                                            values.includes(s.value),
                                          );
                                        field.onChange(
                                          allValuesSelected
                                            ? []
                                            : sponsorships.map(s => s.value),
                                        );
                                      } else {
                                        // Normal behavior
                                        field.onChange(values);
                                      }
                                    }}
                                    options={sponsorshipsWithSelectAll}
                                  />
                                )}
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setIsSponsorshipCalendarVisible(true)
                              }
                              className="mcfont mt-2 inline-flex items-center text-sm hover:underline text-[#CF4500] hover:text-orange-800">
                              <CalendarDaysIcon
                                className="mr-1 h-4 w-4 shrink-0"
                                aria-hidden="true"
                              />
                              View Sponsorships Calendar
                            </button>
                          </div>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Special Collections
                            </label>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('ecoFriendly')}
                                      type="checkbox"
                                      key={'ecoFriendly'}
                                      id={'ecoFriendly'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'ecoFriendly'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    Eco-friendly Experiences
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('epic')}
                                      type="checkbox"
                                      key={'epic'}
                                      id={'epic'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'epic'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    Epic Experiences
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('isIconic')}
                                      type="checkbox"
                                      key={'isIconic'}
                                      id={'Iconic'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'Iconic'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    Iconic Experiences
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('privilegedAccess')}
                                      type="checkbox"
                                      key={'privilegedAccess'}
                                      id={'privilegedAccess'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'privilegedAccess'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    Privileged Access
                                  </label>
                                </div>
                              </div>
                            </fieldset>

                            <fieldset className="mt-2">
                              <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                                <div className="flex items-center">
                                  <div className="group grid size-4 grid-cols-1">
                                    <input
                                      {...register('specialTreat')}
                                      type="checkbox"
                                      key={'specialTreat'}
                                      id={'specialTreat'}
                                      className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                    />
                                    <svg
                                      fill="none"
                                      viewBox="0 0 14 14"
                                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                      <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-checked:opacity-100"
                                      />
                                      <path
                                        d="M3 7H11"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="opacity-0 group-has-indeterminate:opacity-100"
                                      />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={'specialTreat'}
                                    className="ml-3 block text-sm text-gray-900 font-medium leading-6">
                                    Special Treat
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'exclusions',
                      forceRender: true,
                      label: (
                        <span className="mcfont flex items-center text-sm font-semibold text-gray-900">
                          Exclusions
                          <PanelBadge count={panelSelectionCounts.exclusions} />
                        </span>
                      ),
                      children: (
                        <div className="space-y-4">
                          <fieldset>
                            <div className="space-y-4 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                              <div className="flex items-center">
                                <div className="group grid size-4 grid-cols-1">
                                  <input
                                    {...register(
                                      'exclude_mastercard_collection',
                                    )}
                                    type="checkbox"
                                    id={'EXCLUDE_MASTERCARD_COLLECTION'}
                                    value={'EXCLUDE_MASTERCARD_COLLECTION'}
                                    disabled={isMastercardCollectionSelected}
                                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                  />
                                  <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                    <path
                                      d="M3 8L6 11L11 3.5"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-checked:opacity-100"
                                    />
                                    <path
                                      d="M3 7H11"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-indeterminate:opacity-100"
                                    />
                                  </svg>
                                </div>
                                <label
                                  htmlFor={'EXCLUDE_MASTERCARD_COLLECTION'}
                                  className={classNames(
                                    isMastercardCollectionSelected
                                      ? 'text-gray-400'
                                      : 'text-gray-800',
                                    'ml-3 block text-md font-medium leading-6 ',
                                  )}>
                                  Exclude Mastercard Collection
                                </label>
                              </div>
                            </div>
                            {isMastercardCollectionSelected && (
                              <p className="mt-1 text-xs text-gray-500">
                                Ignored while The Mastercard Collection filter
                                is on.
                              </p>
                            )}
                          </fieldset>

                          <fieldset className="mt-3">
                            <div className="space-y-4 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                              <div className="flex items-center">
                                <div className="group grid size-4 grid-cols-1">
                                  <input
                                    {...register('exclude_content_aggregators')}
                                    type="checkbox"
                                    id={'EXCLUDE_CONTENT_AGGREGATORS'}
                                    value={'EXCLUDE_CONTENT_AGGREGATORS'}
                                    defaultChecked={true}
                                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                  />
                                  <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                    <path
                                      d="M3 8L6 11L11 3.5"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-checked:opacity-100"
                                    />
                                    <path
                                      d="M3 7H11"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-indeterminate:opacity-100"
                                    />
                                  </svg>
                                </div>
                                <label
                                  htmlFor={'EXCLUDE_CONTENT_AGGREGATORS'}
                                  className={classNames(
                                    'text-gray-800',
                                    'ml-3 block text-md font-medium leading-6 ',
                                  )}>
                                  Exclude Content from Aggregator Partners
                                </label>
                              </div>
                            </div>
                          </fieldset>

                          <fieldset className="mt-3">
                            <div className="space-y-4 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                              <div className="flex items-center">
                                <div className="group grid size-4 grid-cols-1">
                                  <input
                                    {...register('exclude_benefits')}
                                    type="checkbox"
                                    id={'EXCLUDE_BENEFITS'}
                                    value={'EXCLUDE_BENEFITS'}
                                    defaultChecked={true}
                                    disabled={contentTypeIncludesBenefits}
                                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                  />
                                  <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                    <path
                                      d="M3 8L6 11L11 3.5"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-checked:opacity-100"
                                    />
                                    <path
                                      d="M3 7H11"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-indeterminate:opacity-100"
                                    />
                                  </svg>
                                </div>
                                <label
                                  htmlFor={'EXCLUDE_BENEFITS'}
                                  className={classNames(
                                    contentTypeIncludesBenefits
                                      ? 'text-gray-400'
                                      : 'text-gray-800',
                                    'ml-3 block text-md font-medium leading-6 ',
                                  )}>
                                  Exclude Benefits
                                </label>
                              </div>
                            </div>
                            {contentTypeIncludesBenefits && (
                              <p className="mt-1 text-xs text-gray-500">
                                Ignored while Content Type includes Benefits.
                              </p>
                            )}
                          </fieldset>

                          <fieldset className="mt-3">
                            <div className="space-y-4 sm:flex sm:items-center sm:space-x-7 sm:space-y-0">
                              <div className="flex items-center">
                                <div className="group grid size-4 grid-cols-1">
                                  <input
                                    {...register('exclude_travel_benefits')}
                                    type="checkbox"
                                    id={'EXCLUDE_TRAVEL_BENEFITS'}
                                    value={'EXCLUDE_TRAVEL_BENEFITS'}
                                    defaultChecked={true}
                                    className="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-orange-400 checked:bg-orange-400 indeterminate:border-orange-400 indeterminate:bg-orange-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                                  />
                                  <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25">
                                    <path
                                      d="M3 8L6 11L11 3.5"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-checked:opacity-100"
                                    />
                                    <path
                                      d="M3 7H11"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="opacity-0 group-has-indeterminate:opacity-100"
                                    />
                                  </svg>
                                </div>
                                <label
                                  htmlFor={'EXCLUDE_TRAVEL_BENEFITS'}
                                  className={classNames(
                                    'text-gray-800',
                                    'ml-3 block text-md font-medium leading-6 ',
                                  )}>
                                  Exclude Travel Benefits (MTR)
                                </label>
                              </div>
                            </div>
                          </fieldset>

                          <div className="col-span-full">
                            <label
                              htmlFor="about"
                              className="mb-2 block text-sm font-semibold leading-6 text-gray-900">
                              Exclude Content Source Programs
                            </label>

                            <Controller
                              name="excludeProgramNames"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  defaultValue={field.defaultValue}
                                  mode="multiple"
                                  allowClear
                                  style={{ width: '100%' }}
                                  placeholder="Select programs to exclude"
                                  onChange={values => field.onChange(values)}
                                  options={excludableProgramOptions}
                                />
                              )}
                            />
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />

                {isAdmin(user) && (
                  <>
                    <Divider />
                    <div className="col-span-full">
                      <Switch.Group>
                        <div className="flex items-center justify-between mt-5">
                          <Switch.Label className="mr-4 mcfont font-bold text-sm text-gray-700 ">
                            Show results as table:
                          </Switch.Label>
                          <Switch
                            checked={showTable}
                            onChange={setShowTable}
                            className={`${
                              showTable ? 'bg-orange-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}>
                            <span
                              className={`${
                                showTable ? 'translate-x-6' : 'translate-x-1'
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                      </Switch.Group>
                    </div>
                  </>
                )}
              </form>
            </div>
            <div className="min-w-0 flex-1">
              {showTable ? (
                <Table
                  columns={columns}
                  size={'middle'}
                  loading={loading}
                  className="rounded-md shadow-md shadow-slate-200"
                  rowClassName={(record, index) =>
                    index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                  }
                  pagination={{
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    pageSize: tablePageSize,
                  }}
                  onRow={record => {
                    return {
                      onClick: () => {
                        handleShowDetails(record);
                      },
                    };
                  }}
                  onChange={handleTableChange}
                  locale={{
                    emptyText: 'No results were found for your search.',
                  }}
                  dataSource={filteredData}
                  rowKey="id"
                />
              ) : loading ? (
                <div className="flex flex-col items-center mt-24">
                  <svg
                    className="w-20 h-20 text-orange-600 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="absolute inset-0 bg-transparent pointer-events-auto"></div>
                </div>
              ) : filteredData.length === 0 ? (
                <div className="flex flex-col items-center mt-24">
                  <p className="text-2xl mcfont font-semibold text-gray-500">
                    No results found
                  </p>
                  <p className="text-gray-400 mcfont">
                    Try adjusting your search filters
                  </p>
                </div>
              ) : (
                <PaginatedItems
                  data={flattenGroupedData(groupedData)}
                  itemsPerPage={18}
                  handleShowDetails={handleShowDetails}
                />
              )}
            </div>
            <Modal
              title={
                selectedRowData && (
                  <div className="mcfont text-lg">
                    {cleanString(selectedRowData.title)}
                  </div>
                )
              }
              open={isProductInfoVisible}
              width={700}
              onCancel={handleModalClose}
              footer={[
                <Button key="back" onClick={handleModalClose}>
                  Return
                </Button>,
              ]}>
              {selectedRowData && (
                <div>
                  <ProductInfoView data={selectedRowData} />
                </div>
              )}
            </Modal>
            <Modal
              title={
                <div className="mcfont text-2xl font-semibold">
                  Your Search Summary
                </div>
              }
              open={isSummaryModalVisible}
              width="80%"
              onCancel={handleSummaryModalClose}
              footer={[
                <Button key="back" onClick={handleSummaryModalClose}>
                  Return
                </Button>,
              ]}>
              <ContentSummary
                summary={summary}
                currentSearchQuery={listOfFiltersFromFormData()}
              />
            </Modal>
            <Modal
              title={
                <div className="mcfont text-2xl font-semibold">
                  2026 Sponsorships Calendar
                </div>
              }
              open={isSponsorshipCalendarVisible}
              width="80%"
              onCancel={() => setIsSponsorshipCalendarVisible(false)}
              footer={[
                <Button
                  key="back"
                  onClick={() => setIsSponsorshipCalendarVisible(false)}>
                  Return
                </Button>,
              ]}>
              <div className="space-y-6">
                <div>
                  <img
                    src="/img/sponsorships_calendar_2026.jpg"
                    alt="2026 Sponsorships Calendar"
                    className="w-full rounded-md"
                  />
                  <a
                    href="/img/sponsorships_calendar_2026.jpg"
                    target="_blank"
                    rel="noreferrer"
                    className="mcfont mt-1 inline-block text-sm underline font-semibold text-[#CF4500] hover:text-orange-800">
                    Open full size
                  </a>
                </div>
                <div>
                  <img
                    src="/img/f1_race_calendar_2026.jpg"
                    alt="2026 Formula 1 Race Calendar"
                    className="w-full rounded-md"
                  />
                  <a
                    href="/img/f1_race_calendar_2026.jpg"
                    target="_blank"
                    rel="noreferrer"
                    className="mcfont mt-1 inline-block text-sm underline font-semibold text-[#CF4500] hover:text-orange-800">
                    Open full size
                  </a>
                </div>
              </div>
            </Modal>

            {/* Scoped theme: the tour's primary button uses the brand action
                orange instead of the pastel global colorPrimary. */}
            <ConfigProvider theme={{ token: { colorPrimary: '#CF4500' } }}>
              <Tour
                open={isTourOpen}
                onClose={dismissTour}
                onFinish={dismissTour}
                onChange={handleTourStepChange}
                steps={tourSteps}
              />
            </ConfigProvider>
          </div>
        </div>
      </main>

      <footer className="border-t border-orange-200 bg-orange-50">
        <div className="flex w-full items-center justify-center gap-2 px-4 py-3 sm:px-6 lg:px-8">
          <InformationCircleIcon
            className="h-5 w-5 shrink-0 text-orange-700"
            aria-hidden="true"
          />
          <p className="mcfont text-center text-sm font-medium text-orange-900">
            The content catalog reflects live data from Priceless.com. If an
            item is not shown here, it is not currently available in the
            database or may not be set to Active.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SearchResults;
